import type { Timestamp } from 'firebase/firestore';

export function formatTimestamp(ts?: Timestamp | null): string {
  if (!ts) return '-';
  const date = ts.toDate();
  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/**
 * How long ago, in words — "4 minutes ago", "2 hours ago", "yesterday". Older than a
 * week reads as a plain date, because "37 days ago" is harder to place than "12 Aug".
 * Returns '' for a missing timestamp so callers can drop the line entirely.
 */
export function formatRelative(ts?: Timestamp | null, now: Date = new Date()): string {
  if (!ts) return '';
  const date = ts.toDate();
  const seconds = Math.round((now.getTime() - date.getTime()) / 1000);
  if (!Number.isFinite(seconds)) return '';
  if (seconds < 0) return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function formatEpochSeconds(seconds?: number | null): string {
  if (!seconds) return '-';
  return new Date(seconds * 1000).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function formatCoords(lat?: number | null, lon?: number | null): string {
  if (lat == null || lon == null) return '-';
  return `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
}

export function formatMoneyPence(
  amountPence?: number | null,
  currency = 'GBP'
): string {
  if (amountPence == null || Number.isNaN(amountPence)) return '-';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(amountPence / 100);
}

export function titleCaseStatus(value?: string | null): string {
  if (!value) return '-';
  return value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
